import React from "react";
import { Router, useRouter } from "next/router";
import DiagnosisTemplete from "components/templates/common/Detail/Diagnosis/Index";
import DiagnosisMainSection from "components/templates/common/Detail/Diagnosis/Main";
import DiagnosisMoreSection, {
  FinInfoComponent,
  ToggleComponent,
} from "components/templates/common/Detail/Diagnosis/More";
import { TYPO } from "styles/Typo";
import {
  DETAIL_FIN_STABILITY_DESC_TABLE,
  StabilityInfo,
} from "utils/constant/STATUS";
import { useFinanceData } from "utils/clientAPIs/detailDiagnosis";
import { useParams } from "next/navigation";
import dynamic from "next/dynamic";
import {
  BadIcon,
  convertSubjectEngName,
  convertToQuarter,
  GoodIcon,
  HelpIcon,
} from "./util";
import { TAILWIND_TO_CSS } from "utils/constant/STYLE";
import { useTranslation } from "next-i18next";

const DynamicGaugeSocre: React.FC<any> = dynamic(
  () => import("components/templates/common/Detail/Diagnosis/GaugeSocre"),
  {
    loading: () => <div className="h-[222px]"></div>,
    ssr: false,
  }
) as any;

//조, 억, 만 단위 변환
export function convertToKoreanUnit(value: number, router: any) {
  const absValue = Math.abs(value);

  const isMinus = value < 0 ? "-" : "";
  if (router.locale === "ko") {
    const trillion = 1000000000000;
    const billion = 100000000;
    const tenThousand = 10000;

    if (absValue >= trillion) {
      const trillionPart = Math.floor(absValue / trillion);
      const billionPart = Math.floor((absValue % trillion) / billion);

      // "조"와 "억"을 모두 표시하되, "억" 부분이 0이면 생략
      return billionPart > 0
        ? `${isMinus}${trillionPart.toLocaleString(
            "en-us"
          )}조 ${billionPart.toLocaleString("en-us")}억`
        : `${trillionPart.toLocaleString("en-us")}조`;
    } else if (absValue >= billion) {
      // 억
      return `${isMinus}${Math.floor(absValue / billion)?.toLocaleString(
        "en-us"
      )}억`;
    } else if (absValue >= tenThousand) {
      // 만
      return `${isMinus}${Math.floor(absValue / tenThousand)?.toLocaleString(
        "en-us"
      )}만`;
    }
  } else {
    const billion = 1000000000;
    const million = 1000000;
    const thousand = 1000;

    if (absValue >= billion) {
      return `${isMinus}${(absValue / billion).toLocaleString("en-us", {
        maximumFractionDigits: 1,
      })}B`;
    } else if (absValue >= million) {
      return `${isMinus}${(absValue / million).toLocaleString("en-us", {
        maximumFractionDigits: 1,
      })}M`;
    } else if (absValue >= thousand) {
      return `${isMinus}${(absValue / thousand).toLocaleString("en-us", {
        maximumFractionDigits: 1,
      })}K`;
    }
    return `${isMinus}${absValue.toLocaleString("en-us")}`;
  }
}

type INDI_NAME_TYPE =
  | "현금흐름보상비율"
  | "부채비율"
  | "재고자산회전율"
  | "총자산회전율"
  | "유동자산회전율"
  | "매출총이익률"
  | "3년간 영업현금흐름";

export function createFieldContents(
  content: any,
  index: number,
  isUsdData: boolean,
  router: any
) {
  const field = content.field;
  const status = content.INDI_STAT;
  const INDI_NAME: INDI_NAME_TYPE = content?.INDI_NAME;

  const fieldObject = DETAIL_FIN_STABILITY_DESC_TABLE.find(
    (item: any) => item.field === field
  );

  const fieldEng = fieldObject?.fieldEng;
  const title = fieldObject?.title;
  const titleEng = fieldObject?.titleEng;

  const ACCOUNT_SUBJECT = content?.ACCOUNT_SUBJECT || []; // array
  const find_ACCOUNT_SUBJECT_DESC = (
    field: string,
    name: keyof StabilityInfo
  ) => {
    return DETAIL_FIN_STABILITY_DESC_TABLE.find(
      (item: any) => item.field === field
    )?.[INDI_NAME]?.[name];
  };

  const IDX_VAL = content?.IDX_VAL;
  const IDX_DESC =
    router.locale === "ko"
      ? fieldObject?.[INDI_NAME]?.[status ? "안정" : "불안정"]
      : fieldObject?.[INDI_NAME]?.[status ? "안정eng" : "불안정eng"];

  const fieldClass =
    field === "안정성"
      ? "stability"
      : field === "수익성"
      ? "profit"
      : field === "활동성"
      ? "activity"
      : "cash";

  return {
    subDescription: router.locale === "ko" ? title : titleEng,
    Question: (
      <span
        className={`${TYPO.TYPO5_1_SB} text-gray70 detail-fin-index detail-fin-char-name`}
      >
        {router.locale === "ko" ? field : fieldEng}
      </span>
    ),
    index: index,
    // centerContent: status ? <GoodIcon /> : <BadIcon />,
    toggleOpenWithChildContents: ({
      setDescTooltip,
      descTooltip,
      tooltipRef,
    }: any): any => (
      <div>
        <div
          className={`each-item flex flex-col  gap-5 w-full pt-6 px-6`}
          data-testid={`each-item`}
        >
          <span className={`${TYPO.TYPO6_SB} text-gray90 detail-fin-index `}>
            {router.locale === "ko" ? field : fieldEng}
          </span>
          {IDX_DESC && (
            <span
              className={`${TYPO.TYPO7_MD} ${
                status ? "text-blue40" : "text-red40"
              } detail-fin-char-desc break-keep`}
            >
              {INDI_NAME === "부채비율"
                ? !status
                  ? IDX_VAL < 180
                    ? IDX_DESC[0]
                    : IDX_DESC[1]
                  : IDX_DESC
                : IDX_DESC}
            </span>
          )}
        </div>
        <div className="px-6 py-4">
          <div className="each-item flex flex-col justify-center items-center gap-1 bg-gray2 rounded-[12px] p-5">
            <span
              className={`${TYPO.TYPO5_1_MD} text-gray60  detail-fin-char-name`}
            >
              {router.locale === "ko"
                ? INDI_NAME
                : convertSubjectEngName(INDI_NAME)}
            </span>

            <span
              className={`${TYPO.TYPO5_SB} ${
                status ? "text-blue40" : "text-red40"
              }  detail-fin-char-value`}
            >
              {INDI_NAME === "3년간 영업현금흐름" && field === "현금흐름"
                ? isUsdData
                  ? `${Number(IDX_VAL) < 0 ? "-" : ""}$${Math.abs(
                      Number(IDX_VAL)
                    ).toLocaleString("en-us")}`
                  : convertToKoreanUnit(IDX_VAL, router)
                : `${Number(Number(IDX_VAL).toFixed(1))?.toLocaleString(
                    "en-us"
                  )}%`}
            </span>
          </div>
        </div>
        <div className={`py-2 mx-6 ${fieldClass}`} data-testid={index}>
          <div className="flex gap-4">
            <div className="flex flex-col gap-2 w-full">
              {!isUsdData &&
                ACCOUNT_SUBJECT?.map((item: any, index: number) => {
                  const key = Object.keys(item)[0] as keyof StabilityInfo;
                  const value = item[key];
                  return (
                    <div
                      key={index}
                      className="each-item w-full flex items-center py-2 gap-3"
                    >
                      <div className="flex gap-1 items-center grow">
                        <span
                          className={` ${TYPO.TYPO7_MD} text-gray60 detail-fin-subject-name`}
                        >
                          {router.locale === "ko"
                            ? key
                            : convertSubjectEngName(key)}
                        </span>
                        <div
                          className={` cursor-pointer relative `}
                          onClick={() => {
                            setDescTooltip({
                              index: index,
                              status: true,
                            });
                          }}
                          data-gtm-id={`sDetail-fin-tooltip`}
                        >
                          {router.locale === "ko" && (
                            <>
                              <HelpIcon />
                              <div
                                ref={tooltipRef}
                                style={{
                                  boxShadow:
                                    "0px 0px 16px 8px rgba(59, 71, 84, 0.08)",
                                }}
                                className={`${
                                  descTooltip.index === index &&
                                  descTooltip.status
                                    ? "block"
                                    : "hidden"
                                } z-30 absolute top-[-16px] left-[22px]  bg-white p-2 rounded-[8px] w-fit ]`}
                              >
                                <span
                                  className={`${TYPO.TYPO8_MD} text-gray40 whitespace-nowrap`}
                                >
                                  {find_ACCOUNT_SUBJECT_DESC(field, key)}
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      <span
                        className={`${TYPO.TYPO7_MD} text-gray50 detail-fin-subject-value`}
                      >
                        {convertToKoreanUnit(value, router)}
                      </span>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>
    ),
  };
}

export const IndexBarChart = ({ WEIGHTED_INDI_PER, router }: any) => {
  const metaData = [
    { field: "안정성", total: 50, height: 104 },
    { field: "수익성", total: 20, height: 50 },
    { field: "활동성", total: 20, height: 50 },
    { field: "현금흐름", total: 10, height: 20 },
    ,
  ];
  const WEIGHTED_INDI_PER_LIST = WEIGHTED_INDI_PER?.map((item: any) => {
    const height = metaData.find(
      (meta: any) => meta.field === item.field
    )?.height;
    const total = metaData.find(
      (meta: any) => meta.field === item.field
    )?.total;
    return {
      field: item.field,
      value: Math.floor(item.WEIGHTED_INDI_PER),
      height: height,
      total: total,
      INDI_STATE: item.INDI_STATE,
    };
  });

  const calcurateGauge = (
    scroe: number,
    totalScore: number,
    height: number
  ) => {
    if (totalScore === 50) {
      if (scroe === 0) return 0;
      if (scroe <= 7) return 16;
      if (scroe <= 16) return 40;
      if (scroe <= 23) return 50;
      if (scroe <= 26) return 60;
      if (scroe <= 31) return 70;
      if (scroe <= 41) return 80;
      if (scroe <= 50) return height;
    } else if (totalScore === 20) {
      if (scroe === 0) return 0;
      if (scroe <= 3) return 14;
      if (scroe <= 8) return 20;
      if (scroe <= 13) return 30;
      if (scroe <= 19) return 40;
      if (scroe === 20) return height;
    } else if (totalScore === 10) {
      if (scroe === 0) return 0;

      if (scroe === 10) return height;
    }
    return (scroe / totalScore) * height;
  };

  return (
    <div className="flex items-end gap-4">
      {WEIGHTED_INDI_PER_LIST.map((item: any, index: number) => (
        <div
          key={index}
          className="flex flex-col justify-center items-center gap-2 grow"
        >
          <div className={`${TYPO.TYPO7_MD}`}>
            <span
              className={`${
                item.INDI_STATE ? "text-blue40" : "text-gray50"
              } detail-fin-index-score`}
            >
              {item.value}
              {router.locale === "ko" ? "점" : ""}
            </span>
          </div>
          <div
            style={{
              height: item.height + "px",
            }}
            className=" w-[54px] relative bg-gray10 rounded-[8px]"
          >
            <div
              style={{
                height:
                  calcurateGauge(item.value, item.total, item.height) + "px",
                backgroundColor: item.INDI_STATE
                  ? TAILWIND_TO_CSS["blue40"]
                  : TAILWIND_TO_CSS["gray40"],
              }}
              className="absolute  w-full bottom-0 rounded-[8px]"
            ></div>
          </div>

          <div className="flex flex-col items-center justify-center">
            <span className={`${TYPO.TYPO7_MD} text-gray60`}>
              {router.locale === "ko"
                ? item.field
                : convertSubjectEngName(item.field)}
            </span>
            <span className={`text-gray30`}>{item.total}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

function DetailFin({ detailData, isMobile }: any) {
  const router = useRouter();
  const { t } = useTranslation("detail");
  const { ticker } = useParams();
  const ITEM_CD = detailData?.ITEM_CD;
  const [finData, isLoading] = useFinanceData(ticker as string, ITEM_CD);

  const totalFinScore = 100;
  const finScore = finData?.finScore; // 재무점수
  const REPT_PERIOD = finData?.REPT_PERIOD; // 분기
  const isUsdData = finData?.isUsdData; // 달러화폐
  const finScoreCompare = finData?.finScoreCompare; // 재무점수 비교

  // 지표 정렬
  const so = ["안정성", "수익성", "활동성", "현금흐름"];
  const sortValues = finData?.values?.sort((a: any, b: any) => {
    const indexA = so.indexOf(a.field);
    const indexB = so.indexOf(b.field);
    return (
      (indexA === -1 ? so.length : indexA) -
      (indexB === -1 ? so.length : indexB)
    );
  });
  const WEIGHTED_INDI_PER = sortValues?.map((item: any) => {
    return {
      field: item.field,
      WEIGHTED_INDI_PER: item.WEIGHTED_INDI_PER,
      INDI_STATE: sortValues.find((value: any) => value.field === item.field)
        ?.INDI_STAT,
    };
  });

  // 지표 컴포넌트 생성
  const contents = sortValues?.map((content: any, index: number) =>
    createFieldContents(content, index, isUsdData, router)
  );
  const allContents = contents?.map((content: any, index: number) => (
    <ToggleComponent {...content} key={index} />
  ));
  return (
    <DiagnosisTemplete
      isLoading={isLoading}
      data={finData}
      tickerName={
        router.locale === "ko"
          ? detailData?.ITEM_KR_NM
          : detailData?.ITEM_ENG_NM
      }
      category={{
        name: t("financialSoundness"),
        date: convertToQuarter(REPT_PERIOD, router),
        score: finScore,
        description: t("finDesc"),
      }}
      isMobile={isMobile}
      ticker={ticker as string}
    >
      <>
        {/* <DynamicGaugeSocre
          score={finScore}
          totalScore={totalFinScore}
          rankChange={
            finScoreCompare || finScoreCompare === 0
              ? router.locale === "ko"
                ? "직전 분기보다 " +
                  `${finScoreCompare >= 0 ? "+" : ""}` +
                  finScoreCompare
                : `${finScoreCompare >= 0 ? "+" : ""}` +
                  finScoreCompare +
                  " from previous quarter"
              : ""
          }
        />

        <DiagnosisMainSection
          contentsName="재무건전성"
          contents={{
            date: t("assessmentIndicators"),
            // buttonText: t("findStocksWithfin"),
            chart: (
              <IndexBarChart
                WEIGHTED_INDI_PER={WEIGHTED_INDI_PER}
                router={router}
              />
            ),
          }}
          buttonClickHandler={() => {
            // router.push("/");
          }}
        /> */}
        {/* <DiagnosisMoreSection toggleContents={allContents} /> */}
        {contents?.map((content: any, index: number) => (
          <FinInfoComponent {...content} key={index} index={index} />
        ))}
      </>
    </DiagnosisTemplete>
  );
}

export default DetailFin;
